import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box
} from '@mui/material';
import { X, Save, Plus } from 'lucide-react';

const QuoteForm = ({ open, onClose, onSubmit }) => {
    // Form state
    const [formData, setFormData] = useState({
        customer: '',
        status: 'Draft',
        description: '',
        amount: '',
        contact_email: '',
        contact_phone: ''
    });

    // Status options
    const statusOptions = ['Draft', 'Pending', 'In Review', 'Approved', 'Rejected'];

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();

        // Generate a random quote ID (in a real app, this would come from the backend)
        const quoteId = `Q${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;

        // Create the complete quote object
        const newQuote = {
            id: quoteId,
            customer: formData.customer,
            status: formData.status,
            description: formData.description,
            amount: formData.amount ? parseFloat(formData.amount) : 0,
            contact_email: formData.contact_email,
            contact_phone: formData.contact_phone,
            date_issued: new Date().toISOString().split('T')[0]
        };

        // Create an initial log entry
        const initialLog = {
            quote_id: quoteId,
            message: `Quote created for ${formData.customer}`,
            date_logged: new Date().toISOString().split('T')[0],
            type: 'creation'
        };

        // Submit the new quote and log
        onSubmit(newQuote, initialLog);

        // Reset form and close dialog
        setFormData({
            customer: '',
            status: 'Draft',
            description: '',
            amount: '',
            contact_email: '',
            contact_phone: ''
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Plus size={18} />
                    New Quote
                </Box>
                <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={onClose}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                >
                    <X size={18} />
                </Button>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            name="customer"
                            label="Customer Name"
                            value={formData.customer}
                            onChange={handleChange}
                            required
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            sx={{ gridColumn: '1 / -1' }}
                        />

                        <FormControl margin="dense" required>
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                label="Status"
                            >
                                {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>{status}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            name="amount"
                            label="Amount"
                            type="number"
                            value={formData.amount}
                            onChange={handleChange}
                            InputProps={{
                                startAdornment: <span style={{ marginRight: 4 }}>$</span>
                            }}
                            fullWidth
                            margin="dense"
                        />

                        <TextField
                            name="description"
                            label="Description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            sx={{ gridColumn: '1 / -1' }}
                        />

                        <TextField
                            name="contact_email"
                            label="Contact Email"
                            type="email"
                            value={formData.contact_email}
                            onChange={handleChange}
                            fullWidth
                            margin="dense"
                        />

                        <TextField
                            name="contact_phone"
                            label="Contact Phone"
                            value={formData.contact_phone}
                            onChange={handleChange}
                            fullWidth
                            margin="dense"
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} variant="outlined" color="inherit" startIcon={<X size={16} />}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary" startIcon={<Save size={16} />}>
                        Create Quote
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default QuoteForm;